import React, { useState } from 'react'
import {
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaPlus,
} from 'react-icons/fa'
import { FaCircleXmark } from 'react-icons/fa6'
import { parseMilitaryTimeToAMPM } from '@utils'
import { formatCutoff } from '@utils/groupOrderUtils'
import { MenuConcept } from './types'
import { getAvailableDaysStr, getDietaryCountsArray } from './utils'
import { DietCountsCell, MenuTable, MenuItemsSubtable } from './MenuTable'

export interface StagedMenusSectionProps {
  stagedMenus: MenuConcept[]
  isLoading: boolean
  isProcessing: boolean
  onRemove: (menuId: string) => void
  onFinalize: () => void
}

export const StagedMenusSection: React.FC<StagedMenusSectionProps> = ({
  stagedMenus,
  isLoading,
  isProcessing,
  onRemove,
  onFinalize,
}) => {
  const [showAllDietary, setShowAllDietary] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)

      return next
    })

  const getStagingSummary = () => {
    const totalItemCount = stagedMenus.reduce(
      (sum, menu) => sum + menu.menuItems.length,
      0,
    )
    const dietarySummary: Record<string, number> = {}
    const cuisineSummary: Record<string, number> = {}

    stagedMenus.forEach((menu) => {
      Object.entries(menu.dietaryCounts).forEach(([dietary, count]) => {
        dietarySummary[dietary] = (dietarySummary[dietary] || 0) + count
      })
      menu.cuisines.forEach((cuisine) => {
        cuisineSummary[cuisine] = (cuisineSummary[cuisine] || 0) + 1
      })
    })

    return { totalItemCount, dietarySummary, cuisineSummary }
  }

  const renderRow = (menu: MenuConcept) => {
    const open = expanded.has(menu.id)

    return (
      <>
        <tr className="border-b border-t border-gray-400 border-solid bg-gray-100">
          <td className="flex flex-col px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggle(menu.id)}
                className="focus:outline-none focus:ring-2 focus:ring-teal-500"
                aria-expanded={open}
                title={open ? 'Collapse items' : 'Expand items'}
              >
                {open ? (
                  <FaChevronUp className="h-4 w-4" />
                ) : (
                  <FaChevronDown className="h-4 w-4" />
                )}
              </button>

              <div className="flex flex-col">
                <p className="pb-1">{menu.name}</p>
                <button
                  onClick={() => onRemove(menu.id)}
                  disabled={isProcessing}
                  className="px-2 py-1 bg-red-500 text-white rounded-md text-xs font-bold hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:bg-gray-400 w-fit"
                  title="Remove from staging"
                >
                  Remove
                </button>
              </div>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.chef.fullName || menu.chef.name}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div className="flex gap-1 flex-wrap">
              {menu.cuisines.map((tag) => (
                <div
                  key={tag}
                  className="p-1 rounded-lg bg-gray-200 flex items-center"
                >
                  {tag}
                </div>
              ))}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.hasMenuItemWithMods ? (
              <FaCheckCircle color="green" size={20} />
            ) : (
              <FaCircleXmark color="red" size={20} />
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {getAvailableDaysStr(menu)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {formatCutoff(menu.cutoffDays, menu.cutoffHours)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {!menu.pickupHours
              ? 'N/A'
              : parseMilitaryTimeToAMPM(menu.pickupHours)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.maxItems || 'N/A'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.menuItems.length}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.minGoPayout || 'N/A'}
          </td>
          <td className="px-6 py-4 text-sm text-gray-900 flex flex-col">
            <DietCountsCell
              tags={getDietaryCountsArray(menu)}
              showAllTags={showAllDietary}
            />
          </td>
        </tr>

        {open && (
          <tr className="bg-white border-b border-solid border-gray-200">
            <td colSpan={11} className="px-6 py-3">
              <MenuItemsSubtable items={menu.menuItems} />
            </td>
          </tr>
        )}
      </>
    )
  }

  const renderFinalRow = () => {
    const { totalItemCount, dietarySummary, cuisineSummary } =
      getStagingSummary()

    const cuisineChips = Object.entries(cuisineSummary)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

    const dietaryChips = Object.entries(dietarySummary)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

    return (
      <tr className="border-t-4 border-teal-500 border-solid">
        <td
          colSpan={11}
          className="px-6 py-4 bg-gradient-to-r from-teal-100 to-blue-100"
        >
          <div className="rounded-xl bg-white border-4 border-solid border-teal-200 shadow-lg p-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-solid border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-500 text-white">
                  <FaCheckCircle size={20} />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900">
                    Selection Summary
                  </h4>
                  <p className="text-sm text-gray-600">
                    {stagedMenus.length} menu
                    {stagedMenus.length === 1 ? '' : 's'} • {totalItemCount}{' '}
                    total item
                    {totalItemCount === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-lg p-4 border border-solid border-orange-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-orange-500 rounded-full"></div>
                  <h5 className="text-sm font-bold text-gray-800 uppercase tracking-wide bg-">
                    Cuisines
                  </h5>
                </div>
                {cuisineChips.length ? (
                  <div className="flex flex-wrap gap-2">
                    {cuisineChips.map((c) => (
                      <span
                        key={c.label}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white text-gray-800 border-2 border-solid border-orange-300 shadow-sm hover:shadow-md transition-shadow"
                        title={`${c.label} (${c.count} menu${
                          c.count === 1 ? '' : 's'
                        })`}
                      >
                        <span>{c.label}</span>
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full bg-orange-500 text-white text-xs font-bold px-2">
                          {c.count}
                        </span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No cuisine data
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-4 border border-solid border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
                  <h5 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                    Dietary Options
                  </h5>
                </div>
                {dietaryChips.length ? (
                  <div className="flex flex-wrap gap-2">
                    {(showAllDietary
                      ? dietaryChips
                      : dietaryChips.slice(0, 3)
                    ).map((d) => (
                      <span
                        key={d.label}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white text-blue-900 border-2 border-solid border-blue-300 shadow-sm hover:shadow-md transition-shadow"
                        title={`${d.label} (${d.count} item${
                          d.count === 1 ? '' : 's'
                        })`}
                      >
                        <span>{d.label}</span>
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full bg-blue-500 text-white text-xs font-bold px-2">
                          {d.count}
                        </span>
                      </span>
                    ))}
                    {!showAllDietary && dietaryChips.length > 3 && (
                      <button
                        onClick={() => setShowAllDietary(true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-200 text-gray-800 border-2 border-gray-300 hover:bg-gray-300 transition-colors"
                      >
                        <FaPlus size={10} />
                        {dietaryChips.length - 3} more
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No dietary data
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>
      </tr>
    )
  }

  if (stagedMenus.length == 0) {
    return <></>
  }

  return (
    <div className="border-t border-gray-300 pt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Staged Menus ({stagedMenus.length})
        </h3>
      </div>

      <MenuTable
        menus={stagedMenus}
        isLoading={isLoading}
        renderRow={renderRow}
        renderStagingMenusFinalRow={renderFinalRow}
      />

      <button
        onClick={onFinalize}
        disabled={isProcessing || stagedMenus.length === 0}
        className="px-4 py-2 mt-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isProcessing
          ? 'Processing...'
          : `Finalize ${stagedMenus.length} Menu${
              stagedMenus.length === 1 ? '' : 's'
            }`}
      </button>
    </div>
  )
}
